import * as React from 'react';
import Modal from 'react-modal';
import {classNames} from '@shopify/react-utilities/styles';

export interface OnAfterOpen {(props: Props, state: State): any}
export interface OnClose {(props: Props, state: State): any}

export interface Props {
  active?: boolean;
  buttonClassNames?: string[];
  children: React.ReactNode;
  closeButtonClassNames?: string[];
  label: string;
  modalClassNames?: string[];
  onAfterOpen?: OnAfterOpen;
  onClose?: OnClose;
  openByDefault?: boolean;
}

export interface State {
  canUseDom: boolean;
  modalIsOpen: boolean;
}

export class BaseModal extends React.PureComponent<any, any> {
  props: Props;
  state: State;

  APP_ELEMENT_ID = 'PageContainer';

  closeButtonRef: any = null;
  buttonRef: any = null;

  constructor(props: Props) {
    super(props);
    const {openByDefault} = props;
    this.state = {
      canUseDom: false,
      modalIsOpen: Boolean(openByDefault),
    };
    this.props = props;

    // @autobind breaks inherited classes, so we do it the old-fashioned way.
    this.afterOpenModal = this.afterOpenModal.bind(this);
    this.openModal = this.openModal.bind(this);
    this.closeModal = this.closeModal.bind(this);
  }

  componentDidMount() {
    this.setState({canUseDom: true}); // eslint-disable-line react/no-did-mount-set-state
  }

  afterOpenModal() {
    const {onAfterOpen} = this.props;

    if (onAfterOpen) {
      onAfterOpen(this.props, this.state);
    }
  }

  closeModal() {
    const {onClose} = this.props;
    this.setState({modalIsOpen: false});

    if (onClose) {
      onClose(this.props, this.state);
    }
  }

  openModal() {
    this.setState({modalIsOpen: true});
  }

  modalProps() {
    const canUseDOM = Boolean(typeof window !== 'undefined' && window.document && window.document.createElement);
    const {label, modalClassNames} = this.props;
    const {modalIsOpen} = this.state;
    const modalPropsClassName = ['ui-base-modal', 'ui-base-modal--center', ...(modalClassNames || [])];

    const modalProps = {
      appElement: canUseDOM && document.getElementById(this.APP_ELEMENT_ID),
      className: classNames.apply(classNames, modalPropsClassName),
      closeTimeoutMS: 150,
      contentLabel: label,
      isOpen: modalIsOpen,
      onAfterOpen: this.afterOpenModal,
      onRequestClose: this.closeModal,
    };

    return modalProps;
  }

  buttonProps() {
    const {active, buttonClassNames} = this.props;

    const buttonProps = {
      className: classNames.apply(classNames, buttonClassNames),
      disabled: active === false,
      onClick: this.openModal,
      ref: (el) => (this.buttonRef = el),
    };

    return buttonProps;
  }

  render() {
    const {active, children, label} = this.props;

    const buttonProps = this.buttonProps();
    const button = (<button {...buttonProps} type="button">{label}</button>);

    const closeButtonProps = {
      className: 'ui-base-modal__close-button',
      onClick: this.closeModal,
      ref: (el) => (this.closeButtonRef = el),
    };
    const closeButton = (<button {...closeButtonProps} type="button">&times;</button>);

    const modalProps = this.modalProps();
    const modal = (<Modal {...modalProps}>{closeButton}{children}</Modal>);

    const showModal = modalProps.appElement && ((typeof active === 'undefined') || (active === true));

    return (<>{button}{showModal && modal}</>);
  }
}

export default BaseModal;
